import { DataTable } from "primereact/datatable";
import { ProfileUserData, AdditionalUserData } from "../models/adminUsers.model";
import { Column } from "primereact/column";
import { Badge } from "primereact/badge";
import { InputText } from "primereact/inputtext";
import { useState } from "react";
import { Button } from "primereact/button";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Dialog } from "primereact/dialog";

interface Props {
  userData: ProfileUserData[];
  setDataUser: (data: ProfileUserData) => void;
  setDialogEditUser: (data: boolean) => void;
  setDialogChangePasswordUser: (data: boolean) => void;
  setDialogLotteryToUser: (data: boolean) => void;
  findLotteryByUser: (idUser: number) => void;
}

function TableUsers({
  userData,
  setDataUser,
  setDialogEditUser,
  setDialogChangePasswordUser,
  setDialogLotteryToUser,
  findLotteryByUser,
}: Props): JSX.Element {
  const [globalFilter, setGlobalFilter] = useState<string | null>(null);
  const [dialogInfo, setDialogInfo] = useState<boolean>(false);
  const [additionalData, setAdditionalData] = useState<AdditionalUserData | null>(null);
  const [infoUserName, setInfoUserName] = useState<string>("");

  const infoRows: { label: string; value?: string }[] = [
    { label: "Dirección", value: additionalData?.address },
    { label: "Cédula", value: additionalData?.citizenshipId },
    { label: "Ciudad", value: additionalData?.city },
  ];

  // Estilos neutralizados basados exactamente en la paleta de tu Formulario
  const cellStyle = {
    backgroundColor: "#ffffff",
    color: "#334155",
    borderColor: "#e2e8f0",
    padding: "12px 16px",
  };

  const headerCellStyle = {
    backgroundColor: "#f1f5f9",
    color: "#475569",
    borderColor: "#cbd5e1",
    fontWeight: "600",
    padding: "14px 16px",
  };

  const actionButtonStyle = {
    backgroundColor: "#ffffff",
    borderColor: "#cbd5e1",
    color: "#475569",
    width: "2.2rem",
    height: "2.2rem",
  };

  // Encabezado alineado estéticamente al diseño de "Datos del Nuevo Usuario"
  const headerSearch = (
    <div
      className="flex flex-column sm:flex-row justify-content-between align-items-center gap-3 p-2 bg-white"
      style={{ border: "none" }}
    >
      <div className="flex align-items-center gap-3">
        <h3 className="text-xl font-bold m-0" style={{ color: "#334155" }}>
          Usuarios Registrados
        </h3>
        <div
          className="flex align-items-center gap-2 px-2.5 py-1 border-round-md text-xs font-semibold"
          style={{
            backgroundColor: "#f1f5f9",
            color: "#475569",
            border: "1px solid #e2e8f0",
          }}
        >
          <span
            className="w-2 h-2 border-round-full bg-green-500 inline-block"
            style={{ width: "6px", height: "6px" }}
          ></span>
          <span>
            {userData.length} {userData.length === 1 ? "usuario" : "usuarios"}
          </span>
        </div>
      </div>

      <IconField iconPosition="left" className="w-full md:w-25rem">
        <InputIcon className="pi pi-search text-sm" style={{ color: "#94a3b8" }} />
        <InputText
          className="w-full text-left p-inputtext-sm"
          style={{
            backgroundColor: "#ffffff",
            borderColor: "#cbd5e1",
            borderRadius: "6px",
            color: "#334155",
          }}
          type="text"
          placeholder="Buscar usuario..."
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setGlobalFilter(e.target.value);
          }}
        />
      </IconField>
    </div>
  );

  return (
    <div
      className="p-4 bg-white border-round-xl border-1 shadow-1"
      style={{ borderColor: "#e2e8f0" }}
    >
      <DataTable
        value={userData}
        resizableColumns
        showGridlines={false}
        tableStyle={{ minWidth: "50rem", backgroundColor: "#ffffff" }}
        emptyMessage="No hay resultados"
        globalFilter={globalFilter}
        header={headerSearch}
        paginator={userData.length > 5}
        rows={5}
        rowsPerPageOptions={[5, 10, 25, 50]}
        className="p-datatable-sm"
        pt={{
          root: { style: { backgroundColor: "#ffffff" } },
          header: { style: { backgroundColor: "#ffffff", border: "none" } },
          wrapper: { style: { backgroundColor: "#ffffff", border: "none" } },
          table: { style: { backgroundColor: "#ffffff", borderCollapse: "collapse" } },
          tbody: { style: { backgroundColor: "#ffffff" } },
          paginator: {
            root: {
              style: { backgroundColor: "#b9b7b7ff", borderColor: "#e2e8f0", color: "#334155" },
            },
          },
        }}
      >
        <Column
          field="idUser"
          header="ID"
          sortable
          style={{ width: "8%", ...cellStyle, color: "#64748b" }}
          headerStyle={headerCellStyle}
          className="text-center font-medium"
        ></Column>

        <Column
          field="user"
          header="Usuario"
          sortable
          style={{ width: "18%", ...cellStyle, color: "#334155" }}
          headerStyle={headerCellStyle}
          className="font-semibold"
        ></Column>

        <Column
          field="name"
          header="Nombre"
          sortable
          style={{ width: "14%", ...cellStyle }}
          headerStyle={headerCellStyle}
        ></Column>

        <Column
          field="lastName"
          header="Apellido"
          sortable
          style={{ width: "14%", ...cellStyle }}
          headerStyle={headerCellStyle}
        ></Column>

        <Column
          field="phone"
          header="Teléfono"
          sortable
          style={{ width: "14%", ...cellStyle, color: "#475569" }}
          headerStyle={headerCellStyle}
        ></Column>

        <Column
          field="balance"
          header="Saldo"
          sortable
          style={{ width: "12%", ...cellStyle }}
          headerStyle={headerCellStyle}
          body={(data: ProfileUserData) => (
            <span className="font-semibold" style={{ color: "#334155" }}>
              ${Number(data.balance).toLocaleString("es-ES", { minimumFractionDigits: 2 })}
            </span>
          )}
        ></Column>

        <Column
          field="role"
          header="Rol"
          sortable
          style={{ width: "10%", ...cellStyle }}
          headerStyle={headerCellStyle}
          body={(data: ProfileUserData) => (
            <span
              className="text-xs px-2.5 py-1 border-round-md font-bold uppercase tracking-wide border-1"
              style={{ backgroundColor: "#f1f5f9", color: "#475569", borderColor: "#e2e8f0" }}
            >
              {data.role}
            </span>
          )}
        ></Column>

        <Column
          field="state"
          header="Estado"
          sortable
          style={{ width: "10%", ...cellStyle }}
          headerStyle={headerCellStyle}
          body={(data: ProfileUserData) =>
            data.state ? (
              <Badge
                value="Activo"
                style={{ backgroundColor: "#e6f7ed", color: "#00A86B", fontWeight: "600" }}
                className="px-2 py-1 text-xs border-round-md"
              ></Badge>
            ) : (
              <Badge
                value="Inactivo"
                style={{ backgroundColor: "#fef2f2", color: "#ef4444", fontWeight: "600" }}
                className="px-2 py-1 text-xs border-round-md"
              ></Badge>
            )
          }
        ></Column>

        <Column
          header="Acciones"
          style={{ width: "14%", ...cellStyle }}
          headerStyle={{ ...headerCellStyle, textAlign: "center" }}
          body={(data: ProfileUserData) => (
            <div className="flex justify-content-center align-items-center gap-2">
              <Button
                tooltip="Editar usuario"
                tooltipOptions={{ position: "top" }}
                icon="pi pi-pencil"
                className="p-button-rounded shadow-1"
                style={actionButtonStyle}
                onClick={() => {
                  setDataUser(data);
                  setDialogEditUser(true);
                }}
              ></Button>

              <Button
                tooltip="Cambiar contraseña"
                tooltipOptions={{ position: "top" }}
                icon="pi pi-key"
                className="p-button-rounded shadow-1"
                style={actionButtonStyle}
                onClick={() => {
                  setDataUser(data);
                  setDialogChangePasswordUser(true);
                }}
              ></Button>

              <Button
                tooltip={
                  data.additionalInformation
                    ? "Información adicional"
                    : "Sin información adicional"
                }
                tooltipOptions={{ position: "top", showOnDisabled: true }}
                icon="pi pi-info-circle"
                className="p-button-rounded shadow-1"
                disabled={!data.additionalInformation}
                style={actionButtonStyle}
                onClick={() => {
                  setAdditionalData(data.additionalInformation ?? null);
                  setInfoUserName(data.user);
                  setDialogInfo(true);
                }}
              ></Button>
            </div>
          )}
        ></Column>
      </DataTable>

      <Dialog
        header={`Información adicional · ${infoUserName}`}
        visible={dialogInfo}
        style={{
          width: "22rem",
          backgroundColor: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
        }}
        headerStyle={{
          backgroundColor: "#ffffff",
          color: "#334155",
          borderBottom: "1px solid #e2e8f0",
        }}
        contentStyle={{
          backgroundColor: "#ffffff",
          color: "#334155",
          paddingTop: "1.25rem",
        }}
        pt={{
          root: { style: { backgroundColor: "#ffffff", color: "#334155" } },
          header: { style: { backgroundColor: "#ffffff", color: "#334155" } },
          headerTitle: { style: { color: "#334155", fontWeight: "600", fontSize: "1.1rem" } },
          closeButton: { style: { color: "#64748b" } },
          content: { style: { backgroundColor: "#ffffff", color: "#334155" } },
        }}
        breakpoints={{ "575px": "90vw" }}
        modal
        dismissableMask
        draggable={false}
        onHide={() => setDialogInfo(false)}
      >
        <div className="flex flex-column gap-3">
          {infoRows.map((row) => (
            <div key={row.label} className="flex flex-column gap-1">
              <span className="text-xs font-semibold uppercase" style={{ color: "#64748b" }}>
                {row.label}
              </span>
              <span
                className="p-2 border-round-md"
                style={{
                  backgroundColor: "#f1f5f9",
                  color: "#334155",
                  border: "1px solid #e2e8f0",
                }}
              >
                {row.value || "—"}
              </span>
            </div>
          ))}
        </div>
      </Dialog>
    </div>
  );
}

export default TableUsers;